import React, { useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  HStack,
  Tag,
  TagLabel,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { UPDATE_CATEGORIES } from "../index";
import { Partner } from "../types";

interface FormData {
  updateNote: string;
  categories: string[];
}

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPartner: Partner | null;
  isSubmitting: boolean;
  onSubmit: (data: FormData) => Promise<void>;
  isDateEditable: boolean;
}

const UpdateModal: React.FC<UpdateModalProps> = ({
  isOpen,
  onClose,
  selectedPartner,
  isSubmitting,
  onSubmit,
  isDateEditable,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      categories: [],
    },
  });

  const selectedCategories = watch("categories");

  const handleCategoryToggle = (category: string) => {
    const currentCategories = watch("categories");
    if (currentCategories.includes(category)) {
      setValue(
        "categories",
        currentCategories.filter((c) => c !== category)
      );
    } else {
      setValue("categories", [...currentCategories, category]);
    }
  };

  const handleModalClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (selectedPartner?.update) {
      setValue("categories", selectedPartner.update.updateCategories);
      setValue("updateNote", selectedPartner.update.updateNote);
    } else {
      reset();
    }
  }, [selectedPartner, setValue, reset]);

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {selectedPartner?.update ? "Edit Update" : "Add Update"} for{" "}
          {selectedPartner?.companyName}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <FormControl isInvalid={!!errors.categories} mb={4}>
              <FormLabel>Categories</FormLabel>
              <HStack spacing={2} wrap="wrap">
                {UPDATE_CATEGORIES.map((category) => (
                  <Tag
                    key={category.title}
                    colorScheme={category.color}
                    cursor="pointer"
                    variant={
                      selectedCategories.includes(category.title)
                        ? "solid"
                        : "outline"
                    }
                    onClick={() => handleCategoryToggle(category.title)}
                  >
                    <TagLabel>{category.title}</TagLabel>
                  </Tag>
                ))}
              </HStack>
              <FormErrorMessage>
                {errors.categories && "Please select at least one category"}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.updateNote}>
              <FormLabel>Update Note</FormLabel>
              <Textarea
                {...register("updateNote", {
                  required: "Update note is required",
                  minLength: {
                    value: 3,
                    message: "Note must contain at least 3 words",
                  },
                  maxLength: {
                    value: 400,
                    message: "Note must not exceed 400 characters",
                  },
                })}
                placeholder="Enter your update note here..."
                rows={4}
              />
              <FormErrorMessage>
                {errors.updateNote && errors.updateNote.message}
              </FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleModalClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
              isDisabled={!isDateEditable}
            >
              {selectedPartner?.update ? "Update" : "Submit"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UpdateModal;
